/* Repository path: blue_brief_agentic_workflow/frontend/src/components/RecordActual.tsx */

import { useRef, useState, type FormEvent } from "react";
import type { ItemActualVariance, PlanItem } from "../types";
import { formatCurrency, formatDate } from "../utils/format";
import { Button } from "./Button";
import { ChoiceGroup, type Choice } from "./ChoiceGroup";
import { StatusChip } from "./StatusChip";

const MONEY_INPUT = /^\d{1,7}([.,]\d{1,2})?$/;

/** Chip label for the item's confirmed, server-returned state — kept
 * separate from `STATE_OPTIONS`' form labels, which describe a choice to
 * make rather than a fact already recorded. */
const STATE_CHIP_LABEL: Record<string, string> = {
  open: "Recorded",
  received: "Received",
  paid: "Paid",
  completed: "Done",
};

/**
 * State options offered for one item, gated by kind exactly as the server
 * gates them (`planner.actuals.services.valid_states_for_kind`). Never a
 * generic four-way list containing states that make no sense for this
 * item. See docs/architecture/features/W4-06.md, "State is kind-gated".
 */
const STATE_OPTIONS: Record<PlanItem["kind"], Choice[]> = {
  obligation: [
    { value: "open", label: "Still to pay" },
    { value: "paid", label: "Paid" },
  ],
  benefit: [
    { value: "open", label: "Still waiting" },
    { value: "received", label: "Received" },
  ],
  action: [
    { value: "open", label: "Not done yet" },
    { value: "completed", label: "Done" },
  ],
  warning: [
    { value: "open", label: "Not done yet" },
    { value: "completed", label: "Done" },
  ],
};

function amountFieldLabel(item: PlanItem): string {
  const when = item.period === "monthly" ? "this month" : "for the year";
  return item.kind === "benefit" ? `Amount you received ${when}` : `Amount you paid ${when}`;
}

/** Server-computed `variance` turned into the one honest sentence the
 * design calls for — the client never does the arithmetic itself. */
function varianceSentence(variance: ItemActualVariance): string | null {
  switch (variance.direction) {
    case "same":
      return "That’s what we estimated.";
    case "within_range":
      return "That’s within the range we estimated.";
    case "higher":
      return variance.amount
        ? `That’s ${formatCurrency(variance.amount)} more than we estimated.`
        : null;
    case "lower":
      return variance.amount
        ? `That’s ${formatCurrency(variance.amount)} less than we estimated.`
        : null;
    case "not_comparable":
      return variance.against === "none"
        ? "We don’t calculate this one yet, so there’s nothing to compare it with."
        : null;
    default:
      return null;
  }
}

function EstimatedValue({ item }: { item: PlanItem }) {
  if (item.calculation_state !== "calculated") {
    return <>We don’t calculate this one yet</>;
  }
  if (item.min_amount && item.max_amount) {
    return (
      <>
        {formatCurrency(item.min_amount, item.currency)}–{formatCurrency(item.max_amount, item.currency)}
      </>
    );
  }
  return <>{formatCurrency(item.amount as string, item.currency)}</>;
}

/**
 * Comparison, shown only when there is something to compare — an actual
 * amount is recorded. A state recorded alone has nothing to compare
 * against, so no empty "Actual: —" row is ever shown. See
 * docs/design/features/W4-06.md, "Comparison, shown only when there is
 * something to compare".
 */
function EstimatedVsActual({
  item,
  headingRef,
}: {
  item: PlanItem;
  headingRef: React.RefObject<HTMLHeadingElement | null>;
}) {
  const actual = item.actual;
  if (!actual || actual.amount === null) return null;
  const sentence = varianceSentence(actual.variance);

  return (
    <div className="actual-compare">
      <h3 tabIndex={-1} ref={headingRef} id={`actual-compare-title-${item.code}`}>
        Estimated vs actual
      </h3>
      <dl className="facts">
        <div>
          <dt>Estimated</dt>
          <dd>
            <EstimatedValue item={item} />
          </dd>
        </div>
        <div>
          <dt>Actual</dt>
          <dd>
            {formatCurrency(actual.amount, actual.currency)}
            {actual.period === "monthly" ? " a month" : " for the year"}
          </dd>
        </div>
      </dl>
      {sentence && <p className="actual-compare__sentence">{sentence}</p>}
    </div>
  );
}

/** One sentence on whether a settled item still counts toward the forward
 * set-aside — never silent about it either way. `still_contributes` is
 * `null` until a settled state is recorded, so this renders nothing until
 * then. See docs/architecture/features/W4-06.md section 7. */
function StillContributesNote({ item }: { item: PlanItem }) {
  const actual = item.actual;
  if (!actual || actual.still_contributes === null) return null;
  return (
    <p className="field-hint">
      {actual.still_contributes
        ? "This still counts toward what you set aside — we can’t yet confirm it won’t come up again this year."
        : "This no longer counts toward what you set aside for this plan."}
    </p>
  );
}

export interface RecordActualProps {
  item: PlanItem;
  /** `actualAmount` is `null` to record a state with no figure, or to
   * clear a previously-recorded amount while keeping (or changing) the
   * state. */
  onSave: (itemCode: string, state: string, actualAmount: string | null) => Promise<void>;
  /** A full reversal: `state: "open"`, `actual_amount: null`, sent as one
   * idempotent operation, the same as any other record. */
  onUndo: (itemCode: string) => Promise<void>;
}

/**
 * The W4-06 control supplied into `PlanItemDetail`'s `recordActual` slot on
 * a saved plan. Never supplied on a preview — see
 * docs/architecture/features/W4-05.md §7 and
 * docs/architecture/features/W4-06.md, "W4-05's slot is filled without
 * editing PlanItemDetail.tsx".
 */
export function RecordActual({ item, onSave, onUndo }: RecordActualProps) {
  const [amountValue, setAmountValue] = useState(item.actual?.amount ?? "");
  const [stateValue, setStateValue] = useState<string>(item.actual?.state ?? "open");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const compareHeadingRef = useRef<HTMLHeadingElement>(null);
  const amountInputRef = useRef<HTMLInputElement>(null);

  // No resync-on-item-change effect is needed here: `SavedPlanPage`
  // renders `PlanItemDetail` keyed by `item.code`, so navigating to a
  // different item's detail remounts this component fresh via React's own
  // key mechanism. A same-item plan refresh after this component's own
  // save or undo is handled explicitly by those handlers below, so no
  // effect-based sync would ever have anything left to do.
  const hasActiveOverlay = Boolean(item.actual);
  const amountId = `actual-amount-${item.code}`;
  const errorId = `actual-error-${item.code}`;

  const save = async (event: FormEvent) => {
    event.preventDefault();
    const trimmed = amountValue.trim();
    if (trimmed && !MONEY_INPUT.test(trimmed)) {
      setError("Enter an amount like 287.00 or 287,00, or leave it empty.");
      return;
    }
    let normalized: string | null = null;
    if (trimmed) {
      const [whole, fraction = "00"] = trimmed.replace(",", ".").split(".");
      normalized = `${whole}.${fraction.padEnd(2, "0")}`;
    }
    setBusy(true);
    setError(null);
    try {
      await onSave(item.code, stateValue, normalized);
      compareHeadingRef.current?.focus();
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn’t save that. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const undo = async () => {
    setBusy(true);
    setError(null);
    try {
      await onUndo(item.code);
      setAmountValue("");
      setStateValue("open");
      amountInputRef.current?.focus();
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn’t remove that. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="record-actual">
      <p>
        If you’ve had a bill, a payment or a decision, tell us and we’ll use it for what’s ahead.
        Your original estimate stays exactly as it is.
      </p>

      <EstimatedVsActual item={item} headingRef={compareHeadingRef} />
      <StillContributesNote item={item} />

      {hasActiveOverlay && item.actual && (
        <p>
          <StatusChip tone="neutral">{STATE_CHIP_LABEL[item.actual.state] ?? "Recorded"}</StatusChip>{" "}
          Last updated {formatDate(item.actual.recorded_at)}.
        </p>
      )}

      <form className="record-actual__form" onSubmit={(event) => void save(event)}>
        <label htmlFor={amountId}>{amountFieldLabel(item)}</label>
        <div className="record-actual__amount-field">
          <span aria-hidden="true">€</span>
          <input
            id={amountId}
            ref={amountInputRef}
            type="text"
            inputMode="decimal"
            value={amountValue}
            onChange={(event) => setAmountValue(event.target.value)}
            aria-describedby={error ? errorId : `${amountId}-hint`}
            aria-invalid={Boolean(error)}
          />
        </div>
        <p className="field-hint" id={`${amountId}-hint`}>
          Leave this empty if you only want to mark it {STATE_OPTIONS[item.kind][1].label.toLowerCase()}.
        </p>

        <ChoiceGroup
          id={`actual-state-${item.code}`}
          legend="Status"
          choices={STATE_OPTIONS[item.kind]}
          value={stateValue}
          onChange={setStateValue}
        />

        {error && (
          <p className="field-error" id={errorId} role="alert">
            {error}
          </p>
        )}

        <span className="record-actual__actions">
          <Button type="submit" busy={busy}>
            Save what I recorded
          </Button>
          {hasActiveOverlay && (
            <Button type="button" variant="quiet" onClick={() => void undo()} disabled={busy}>
              Undo what I recorded
            </Button>
          )}
        </span>
      </form>
    </div>
  );
}
